/**
 * WebSocket Service for Real-Time Updates
 *
 * Uses the native browser WebSocket so it can speak the backend's
 * { type, data, timestamp } envelope that FastAPI (`api/routes/ws.py`)
 * sends. The earlier implementation used socket.io-client, which could
 * never connect to a raw FastAPI WebSocket — so pipeline events, toasts
 * and the Pipeline Live page were all silent.
 *
 * Public API preserved: connect / disconnect / on / off / emit / send /
 * isSocketConnected / getStatus, plus joinRoom/leaveRoom no-ops for
 * backwards compat.
 */

import toast from 'react-hot-toast'
import { logger } from '../utils/logger'
import { session } from './api'

const MAX_RECONNECT_ATTEMPTS = 10
const BASE_RECONNECT_DELAY_MS = 750
const MAX_RECONNECT_DELAY_MS = 15000
const HEARTBEAT_INTERVAL_MS = 20000

class WebSocketService {
  constructor() {
    this.socket = null
    this.listeners = new Map()
    this.reconnectAttempts = 0
    this.isConnected = false
    this._explicitClose = false
    this._reconnectTimer = null
    this._heartbeatTimer = null
    this._notifiedMaxReconnect = false
  }

  /**
   * Register internal toast/log handlers that run alongside any
   * consumer-provided listeners. Idempotent — called once per instance.
   */
  _registerInternalHandlers() {
    if (this._internalWired) return
    this._internalWired = true

    this.on('deployment_started', (data) => {
      toast.success(`Deployment started: ${data.deployment_id}`, { icon: '🚀', duration: 3000 })
    })
    this.on('deployment_completed', (data) => {
      toast.success(`Deployment ${data.deployment_id} completed successfully!`, { icon: '✅', duration: 5000 })
    })
    this.on('deployment_failed', (data) => {
      toast.error(`Deployment ${data.deployment_id} failed: ${data.error ?? ''}`, { icon: '❌', duration: 5000 })
    })
    this.on('deployment_rolled_back', (data) => {
      toast.error(`Deployment ${data.deployment_id} was rolled back: ${data.reason ?? ''}`, { icon: '⏪', duration: 5000 })
    })
    this.on('vulnerability_detected', (data) => {
      const severity = data.severity?.toUpperCase?.()
      const icon = severity === 'CRITICAL' ? '🚨' : severity === 'HIGH' ? '⚠️' : '🔍'
      toast.error(`New ${severity ?? 'NEW'} vulnerability: ${data.cve_id}`, { icon, duration: 5000 })
    })
    this.on('patch_generated', (data) => {
      const conf = typeof data.confidence === 'number' ? ` (Confidence: ${(data.confidence * 100).toFixed(0)}%)` : ''
      toast.success(`Patch generated for ${data.vulnerability_cve}${conf}`, { icon: '🔧', duration: 4000 })
    })
    this.on('patch_approved', (data) => {
      toast.success(`Patch ${data.patch_id} approved`, { icon: '✅', duration: 3000 })
    })
    this.on('patch_rejected', (data) => {
      toast.error(`Patch ${data.patch_id} rejected: ${data.reason ?? ''}`, { icon: '❌', duration: 4000 })
    })
    this.on('alert', (data) => {
      const severity = data.severity?.toUpperCase?.()
      const icon = severity === 'CRITICAL' ? '🚨' : severity === 'HIGH' ? '⚠️' : 'ℹ️'
      toast.error(data.message, { icon, duration: 5000 })
    })
    this.on('health_degraded', (data) => {
      toast.error(`System health degraded: ${data.service}`, { icon: '⚠️', duration: 4000 })
    })
    this.on('health_restored', (data) => {
      toast.success(`System health restored: ${data.service}`, { icon: '✅', duration: 3000 })
    })
  }

  _buildUrl() {
    // Resolve backend host from env: VITE_WS_BASE_URL wins, otherwise
    // derive from VITE_API_BASE_URL (swap http→ws), otherwise fall back to
    // same origin (dev proxy / single-origin deployments).
    const token = session.getAccessToken() || ''
    const tenant = localStorage.getItem('tenant_id') || ''
    const qs = new URLSearchParams({ token })
    if (tenant) qs.set('tenant', tenant)

    const wsBase = import.meta.env.VITE_WS_BASE_URL
    const apiBase = import.meta.env.VITE_API_BASE_URL

    let origin
    if (wsBase) {
      origin = wsBase.replace(/\/$/, '')
    } else if (apiBase) {
      origin = apiBase.replace(/^http(s?):/, 'ws$1:').replace(/\/$/, '')
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      origin = `${proto}//${window.location.host}`
    }
    return `${origin}/ws/pipeline?${qs.toString()}`
  }

  /**
   * Connect to the backend WebSocket.
   */
  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      logger.websocket?.('already connected')
      return
    }
    this._registerInternalHandlers()
    this._explicitClose = false

    let ws
    try {
      ws = new WebSocket(this._buildUrl())
    } catch (err) {
      logger.websocket?.('construct failed', { error: err?.message })
      this._scheduleReconnect()
      return
    }
    this.socket = ws

    ws.onopen = () => {
      logger.websocket?.('open')
      this.isConnected = true
      this.reconnectAttempts = 0
      this._notifiedMaxReconnect = false
      if (import.meta.env.DEV) {
        toast.success('Real-time connection established', { duration: 2000 })
      }
      this._startHeartbeat()
      this.emit('connected', {})
    }

    ws.onmessage = (event) => {
      let payload
      try {
        payload = JSON.parse(event.data)
      } catch {
        return
      }
      // Backend envelope: { type, data, timestamp }
      // Also accept raw heartbeat/pong envelopes: { type: 'heartbeat' }.
      const type = payload?.type
      if (!type) return
      if (type === 'heartbeat' || type === 'pong') return
      this.emit(type, payload.data ?? payload)
    }

    ws.onerror = (err) => {
      logger.websocket?.('error', { error: err?.message || 'unknown' })
      this.emit('error', { error: err })
    }

    ws.onclose = (event) => {
      logger.websocket?.('close', { code: event.code, reason: event.reason })
      this.isConnected = false
      this._stopHeartbeat()
      this.emit('disconnected', { reason: event.reason, code: event.code })
      if (import.meta.env.DEV && !this._explicitClose) {
        toast.error('Real-time connection lost', { duration: 2000 })
      }
      if (!this._explicitClose) {
        this._scheduleReconnect()
      }
    }
  }

  _scheduleReconnect() {
    if (this._explicitClose) return
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (!this._notifiedMaxReconnect) {
        this._notifiedMaxReconnect = true
        toast.error('Unable to establish real-time connection. Some features may be limited.')
      }
      return
    }
    const attempt = this.reconnectAttempts + 1
    const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY_MS)
    this.reconnectAttempts = attempt
    logger.websocket?.('reconnect scheduled', { attempt, delay })
    clearTimeout(this._reconnectTimer)
    this._reconnectTimer = setTimeout(() => {
      this.connect()
      this.emit('reconnected', { attemptNumber: attempt })
    }, delay)
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this._heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send('ping')
        } catch {
          /* swallow — onerror/onclose will take over */
        }
      }
    }, HEARTBEAT_INTERVAL_MS)
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, [])
    this.listeners.get(event).push(callback)
    return () => this.off(event, callback)
  }

  off(event, callback) {
    const callbacks = this.listeners.get(event)
    if (!callbacks) return
    const idx = callbacks.indexOf(callback)
    if (idx > -1) callbacks.splice(idx, 1)
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event) || []
    callbacks.forEach((cb) => {
      try {
        cb(data)
      } catch (error) {
        logger.error?.(`Error in event listener for ${event}`, error)
      }
    })
  }

  /**
   * Send a raw JSON message to the backend.
   */
  send(event, data) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.warn?.('WebSocket not connected, cannot send message', { event })
      return
    }
    try {
      this.socket.send(JSON.stringify({ type: event, data }))
    } catch (err) {
      logger.warn?.('WebSocket send failed', { event, error: err?.message })
    }
  }

  // Rooms are a socket.io concept. Kept as no-ops for API stability.
  joinRoom() {}
  leaveRoom() {}

  disconnect() {
    this._explicitClose = true
    clearTimeout(this._reconnectTimer)
    this._stopHeartbeat()
    if (this.socket) {
      try {
        this.socket.close(1000, 'client disconnect')
      } catch {
        /* ignore */
      }
      this.socket = null
    }
    this.isConnected = false
    this.listeners.clear()
    this._internalWired = false
    logger.websocket?.('disconnect (manual)')
  }

  isSocketConnected() {
    return this.isConnected && this.socket?.readyState === WebSocket.OPEN
  }

  getStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      socketId: null,
    }
  }
}

const wsService = new WebSocketService()

export default wsService
